import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useInsertReport, useWorldOptions } from '../api/queries';
import {
  type BatchInsertOutcome,
  type BatchItem,
  type InsertOutcome,
  type User,
} from '../api/types';
import { useToast } from '../components/Toast';
import { UnitList } from '../components/UnitList';
import { formatCoords, formatDate } from '../lib/reportFormat';

/** Ein Token oder ein Link, gekürzt für die Anzeige in einer Zeile. */
function shortenReference(reference: string): string {
  if (reference.length <= 40) return reference;
  return `${reference.slice(0, 16)}…${reference.slice(-8)}`;
}

export function InsertPage({ user }: { user: User }) {
  const [text, setText] = useState('');
  const [worldKey, setWorldKey] = useState(user.world?.key ?? '');
  const [result, setResult] = useState<BatchInsertOutcome | null>(null);

  const insert = useInsertReport();
  const toast = useToast();
  const worlds = useWorldOptions('login');

  // Mitglieder hängen an ihrer Welt; nur weltübergreifende Konten wählen aus.
  const needsWorldChoice = user.world === null;

  const error = insert.error instanceof ApiError ? insert.error : null;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setResult(null);

    insert.mutate(
      {
        input: text.trim(),
        ...(needsWorldChoice && worldKey ? { worldKey } : {}),
      },
      {
        onSuccess: (outcome) => {
          setResult(outcome);
          const inserted = outcome.items.filter((item) => item.ok && !item.outcome?.duplicate);
          const failed = outcome.items.filter((item) => !item.ok);

          if (outcome.items.length === 1) {
            const [only] = outcome.items;
            if (!only?.ok) toast.push('error', only?.error ?? 'Bericht konnte nicht eingelesen werden.');
            else if (only.outcome?.duplicate) toast.push('info', 'Dieser Bericht lag bereits in der Datenbank.');
            else toast.push('success', 'Bericht wurde eingelesen.');
          } else {
            toast.push(
              failed.length > 0 ? 'info' : 'success',
              `${inserted.length} von ${outcome.items.length} Berichten neu eingelesen.`,
            );
          }

          if (failed.length === 0) setText('');
        },
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <h1>Bericht einlesen</h1>
        <Link to="/berichte" className="btn btn-ghost">
          Zur Berichtsliste
        </Link>
      </div>
      <p className="muted">
        Veröffentliche einen Bericht im Spiel und füge hier seinen Link ein — oder füge einen
        weitergeleiteten Sammelbericht ein (die <code>[report]…[/report]</code>-Tags mit den
        Berichts-IDs); mehrere Berichte auf einmal werden alle einzeln eingelesen. Das
        funktioniert auch mit Berichten von anderen, etwa von Stammkollegen weitergeleiteten.
      </p>

      {error && (
        <div className="flash flash-error">
          <strong>Nicht eingelesen.</strong> {error.message}
        </div>
      )}

      <form className="form" onSubmit={handleSubmit} noValidate>
        {needsWorldChoice && (
          <label className="field">
            <span>Welt</span>
            <select
              value={worldKey}
              onChange={(event) => setWorldKey(event.target.value)}
              required
            >
              <option value="" disabled>
                Welt wählen …
              </option>
              {(worlds.data?.worlds ?? []).map((world) => (
                <option key={world.key} value={world.key}>
                  {world.name}
                </option>
              ))}
            </select>
            <small className="hint">
              Dein Konto gehört zu keiner Welt — wähle, wofür der Bericht gilt.
            </small>
          </label>
        )}

        <label className="field">
          <span>Link(s) oder weitergeleiteter Bericht</span>
          <textarea
            className="report-input report-url"
            rows={5}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={
              'https://chc1.staemme.ch/public_report/…\n\noder ein weitergeleiteter Sammelbericht mit [report]…[/report]-Tags'
            }
            spellCheck={false}
            autoFocus
            required
          />
          <small className="hint">
            Im Spiel unter dem Bericht: „Dä Bricht vröffentleche" bzw. „Bericht veröffentlichen".
            Ein weitergeleiteter Sammelbericht lässt sich als Ganzes einfügen.
          </small>
        </label>

        <div className="button-row">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              insert.isPending || text.trim().length === 0 || (needsWorldChoice && !worldKey)
            }
          >
            {insert.isPending ? 'Wird geholt …' : 'Einlesen'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={insert.isPending || text.length === 0}
            onClick={() => {
              setText('');
              setResult(null);
              insert.reset();
            }}
          >
            Leeren
          </button>
        </div>
      </form>

      {result && <BatchResults outcome={result} />}
    </section>
  );
}

/**
 * Bei genau einem Bericht bleibt die ausführliche Ansicht (mit Truppen und
 * Verlusten); bei mehreren zeigt eine kompakte Tabelle, was aus jedem
 * einzelnen geworden ist -- eingelesen, schon vorhanden oder fehlgeschlagen.
 */
function BatchResults({ outcome }: { outcome: BatchInsertOutcome }) {
  const { items } = outcome;
  const [only] = items;

  if (items.length === 1 && only) {
    return only.ok && only.outcome ? (
      <SingleResult outcome={only.outcome} />
    ) : (
      <div className="insert-result is-error">
        <p className="result-head">Bericht konnte nicht eingelesen werden.</p>
        <p className="muted">{only.error}</p>
      </div>
    );
  }

  const inserted = items.filter((item) => item.ok && !item.outcome?.duplicate).length;
  const duplicates = items.filter((item) => item.ok && item.outcome?.duplicate).length;
  const failed = items.filter((item) => !item.ok).length;

  return (
    <div className="insert-result">
      <p className="result-head">
        {inserted} von {items.length} Berichten neu eingelesen
        {duplicates > 0 ? `, ${duplicates} bereits vorhanden` : ''}
        {failed > 0 ? `, ${failed} fehlgeschlagen` : ''}.
      </p>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Angreifer</th>
              <th>Verteidiger</th>
              <th>Kampf</th>
              <th>Meldung</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <BatchItemRow key={`${item.reference}-${index}`} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchItemRow({ item }: { item: BatchItem }) {
  if (!item.ok || !item.outcome) {
    return (
      <tr>
        <td>
          <span className="badge badge-error">Fehler</span>
        </td>
        <td colSpan={3} className="muted">
          {shortenReference(item.reference)}
        </td>
        <td className="field-error">{item.error}</td>
      </tr>
    );
  }

  const { report, duplicate, warnings } = item.outcome;

  return (
    <tr>
      <td>
        <span className={`badge ${duplicate ? 'badge-unknown' : 'badge-ok'}`}>
          {duplicate ? 'Duplikat' : 'Eingelesen'}
        </span>
      </td>
      <td>{report.attacker.player ?? '—'}</td>
      <td>{report.defender.player ?? '—'}</td>
      <td className="nowrap">{formatDate(report.battleAt)}</td>
      <td className="muted">{warnings.length > 0 ? warnings.join(' ') : '—'}</td>
    </tr>
  );
}

function SingleResult({ outcome }: { outcome: InsertOutcome }) {
  const { report } = outcome;

  return (
    <div className={`insert-result ${outcome.duplicate ? 'is-duplicate' : ''}`}>
      <p className="result-head">
        {outcome.duplicate
          ? 'Dieser Bericht lag bereits in der Datenbank — es wurde nichts doppelt angelegt.'
          : 'Bericht wurde eingelesen.'}
      </p>

      <dl className="meta-grid">
        <div>
          <dt>Angreifer</dt>
          <dd>
            {report.attacker.player ?? '—'} {formatCoords(report.attacker)}
          </dd>
        </div>
        <div>
          <dt>Verteidiger</dt>
          <dd>
            {report.defender.player ?? '—'} {formatCoords(report.defender)}
          </dd>
        </div>
        <div>
          <dt>Zeitpunkt</dt>
          <dd>{formatDate(report.battleAt)}</dd>
        </div>
        <div>
          <dt>Glück</dt>
          <dd>{report.luck === null ? '—' : `${report.luck} %`}</dd>
        </div>
      </dl>

      <dl className="meta-grid">
        <div>
          <dt>Verluste Angreifer</dt>
          <dd>
            <UnitList counts={report.attacker.losses} />
          </dd>
        </div>
        <div>
          <dt>Verluste Verteidiger</dt>
          <dd>
            <UnitList counts={report.defender.losses} />
          </dd>
        </div>
      </dl>

      {outcome.warnings.length > 0 && (
        <ul className="warning-list">
          {outcome.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

